import * as React from "react";
import { cn } from "@/lib/utils";

interface Stat {
  id: string;
  value: string;
  label: string;
  trend?: string;
}

interface StatsProps {
  stats: Stat[];
}

export function Stats({ stats }: StatsProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 lg:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Divider line */}
        <div className="section-divider mb-16 lg:mb-24" />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {stats.map((stat, index) => (
            <div
              key={stat.id}
              className={cn(
                "text-center group",
                "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              )}
              style={{
                transitionDelay: isVisible ? `${index * 100}ms` : "0ms",
              }}
            >
              {/* Value */}
              <div
                className={cn(
                  "text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight",
                  "text-foreground",
                  "group-hover:text-accent transition-colors"
                )}
              >
                {stat.value}
              </div>

              {/* Label */}
              <div className="mt-2 text-sm sm:text-base text-muted-foreground">
                {stat.label}
              </div>

              {/* Trend indicator */}
              {stat.trend && (
                <div
                  className={cn(
                    "mt-2 inline-flex items-center gap-1",
                    "px-2 py-1 rounded-full",
                    "bg-emerald-500/10 text-emerald-600",
                    "text-xs font-medium"
                  )}
                >
                  {stat.trend}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Divider line */}
        <div className="section-divider mt-16 lg:mt-24" />
      </div>
    </section>
  );
}
